import moment from "moment";
import logger from "./logger";
import {
  DEPENDENT_CHILDREN,
  CHILDREN,
  ENDORSEMENT_ACTIONS,
  GRANDPARENT_ARRAY,
  PARENT_ARRAY,
  PDP_FREEZE_ADDONS,
  PDP_FREEZE_ADDONS_PORT,
  PLAN_TYPES,
  PRODUCER_TYPE,
  PRODUCT_CODE_MAPPING,
  QUOTE_MESSAGES,
  VARIANT_NAME,
} from "@/config/constants";
import { isEmpty } from "lodash";
import {
  ELDERCARE_SUM_INSURED,
  MEDICARE_LITE_SUM_INSURED,
  MEDICARE_PLUS_SUM_INSURED,
  MEDICARE_PREMIER_SUM_INSURED,
  MEDICARE_RESERVE_BASE_DEDUCTIBLE_AND_SUM_INSURED_MAPPING,
  MEDICARE_RESERVE_SUM_INSURED,
  MEDICARE_SELECT_ELITE_SUM_INSURED,
  MEDICARE_SELECT_SUM_INSURED,
  MEDICARE_SUM_INSURED,
  POSP_SUM_INSURED,
  SUM_INSURED_OPTIONS,
  SUPER_CHARGE_SUM_INSURED,
} from "@/config/masterData";
import dayjs from "dayjs";

export const getDefaultDeductible = (sumInsured) => {
  const deductibleArray =
    MEDICARE_RESERVE_BASE_DEDUCTIBLE_AND_SUM_INSURED_MAPPING[sumInsured];
  return deductibleArray && deductibleArray.length > 0
    ? deductibleArray[0]
    : 300000; // DEFAULT_DEDUCTIBLE fallback
};

export const compareTwoStr = (str1, str2) => {
  return str1 && str2 ? str1.toLowerCase() === str2.toLowerCase() : false;
};

export function toAmount(val) {
  if (val === null || val === undefined) return "";

  const num = Number(val);
  if (Number.isNaN(num)) return "";

  return num.toLocaleString("en-IN");
}

export function toAmountFloat(val) {
  if (!val) return null;
  if (val.indexOf(".") !== -1) {
    const valueWithTwoDecimalPlaces = parseFloat(val).toFixed(2);
    const whole = valueWithTwoDecimalPlaces.split(".")[0];
    const decimal = valueWithTwoDecimalPlaces.split(".")[1] || "00";
    const formattedValue = toAmount(whole);
    const finalValue = formattedValue + "." + decimal;
    return finalValue;
  } else {
    return toAmount(val);
  }
}

export const formatDate = (date, format = "YYYY-MM-DD") => {
  let formattedDate = "";
  try {
    formattedDate = moment(date).format(format);
  } catch (err) {
    logger.error("Not a date error occured: ", err);
  }
  return formattedDate;
};

export function convertToDecimal(value, translate = false) {
  let unitLacs = " Lakh";
  let unitThousands = "K";

  return value
    ? +value > 100000
      ? `${Math.floor(+value / 1000) / 100}${unitLacs}`
      : `${Math.floor(+value / 100) / 10}${unitThousands}`
    : value;
}

export function titleCaseFormat(name) {
  if (!name) return null;
  if (typeof name === "string") return name;
  return name
    .replace(/\s{2,}/g, " ")
    .split(" ")
    .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export const getAddressJsx = (
  addressLine1,
  addressLine2,
  addressLine3,
  pinCode
) => {
  const line1 = [addressLine1, addressLine2]
    .filter((s) => s && s.trim().length > 0)
    .join(", ");

  const line2 = [addressLine3, pinCode]
    .filter((s) => s && s.trim().length > 0)
    .join(", ");
  return (
    <>
      {line1}
      <br />
      {line2}
    </>
  );
};

export function spaceSeparatedFormat(productName) {
  if (!productName) return null;
  const displayName = productName.replace(/([A-Z])/g, " $1").trim();
  return displayName;
}

export const calculateAge = (
  dob,
  prefix = "",
  dateFormat = "DD/MM/YYYY",
  referenceDate = null
) => {
  if (dob && dob?.length === 10) {
    const d = moment(dob, dateFormat);
    if (!d.isValid()) {
      return "";
    }

    const refDate = referenceDate
      ? moment(referenceDate, dateFormat)
      : moment();
    const years = refDate.diff(d, "years");

    if (years > 100 || years < 0) {
      return "";
    }
    return `${prefix}${years}`;
  }
  return "";
};

export const calculateAgeDaysYears = (dob, dateFormat = "DD/MM/YYYY") => {
  if (!dob || typeof dob !== "string" || dob.length !== 10) return "";
  const birthDate = moment(dob, dateFormat, true); // strict parsing
  const today = moment();
  if (!birthDate.isValid()) return "";
  const ageInYears = today.diff(birthDate, "years");
  if (ageInYears >= 1) {
    return `${ageInYears}`;
  } else {
    const ageInDays = today.diff(birthDate, "days");
    return `${ageInDays} days`;
  }
};

export const calculateYears = (year1, year2, dateFormat = "DD/MM/YYYY") => {
  if (year1 && year1?.length === 10) {
    const d1 = moment(year1, dateFormat);
    if (!d1.isValid()) {
      return "";
    }
    const d2 = moment(year2, dateFormat);
    if (!d2.isValid()) {
      return "";
    }
    let result = d1.diff(d2, "years");
    return result;
  }
};
export const calculateAgeDays = (dob, dateFormat = "DD/MM/YYYY") => {
  if (dob && dob?.length === 10) {
    const d = moment(dob, dateFormat);
    if (!d.isValid()) {
      return "";
    }
    const days = moment().diff(d, "days");
    return days;
  }
};
export const isFutureDate = (dob) => {
  const enteredDate = moment(dob, "DD/MM/YYYY");
  const currentDate = moment();
  const isFutureDate = enteredDate.isAfter(currentDate);
  return isFutureDate;
};

export const inchesToFt = (inches) => {
  const fts = parseFloat(inches / 12).toFixed(2);
  return fts;
};
export const ftToInches = (ft) => {
  let feet = Math.floor(ft);
  let inch = Math.round((ft - feet) * 12);

  if (inch === 12) {
    feet += 1;
    inch = 0;
  }
  return { feet, inch };
};

export const cmToInches = (cm) => {
  return cm * 0.3937;
};

export const inchesToCm = (inches) => {
  return inches * 2.54;
};
export const cmToFeetAndInches = (cm) => {
  // Conversion factor from centimeters to feet
  const feetConversionFactor = 0.0328084;

  // Step 1: Convert centimeters to feet
  const heightInFeet = cm * feetConversionFactor;

  // Step 2: Extract the whole feet
  const feet = Math.floor(heightInFeet);

  // Step 3: Calculate the remaining inches
  const fractionalFeet = heightInFeet - feet;
  const inches = fractionalFeet * 12;

  // Step 4: Round off inches to 2 decimal places (optional)
  const roundedInches = Math.round(inches * 100) / 100;

  // Return as an object with feet and inches
  return {
    feet: feet,
    inches: roundedInches,
  };
};

export const calculateBmi = (weightInKg, heightInCm) => {
  if (!weightInKg || !heightInCm) return 0;
  return weightInKg / ((heightInCm / 100) * (heightInCm / 100));
};

export const capitalizeFirstCharacter = (str) => {
  if (str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  return "";
};

export const formatAmount = (val) => {
  return Number(val).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const giveLastDurationDate = (count, duration) => {
  let currentDate = moment();
  return moment(currentDate).subtract(count, duration).format("YYYY-MM-DD");
};

export const extractMedicalDocumentName = (
  str,
  showRelation,
  portability = false,
  samePolicy = false,
  isLobPA = false
) => {
  if (str.startsWith("medicalDetails")) {
    let documentName = "Medical Document";
    const match = str.match(/medicalDetails\[(.*?)]\[(.*?)]/);
    const questionNumber = parseInt(match[2].slice(1), 10) - (isLobPA ? 0 : 1);
    let question = parseInt(str.slice(-1), 10) + 1;
    return showRelation
      ? `${documentName} ${question} for ${match[1]} Q${questionNumber}`
      : `${documentName} ${question} for  Q${questionNumber}`;
  } else if (str.startsWith("previousHealthPlan")) {
    let documentName1 = "Previous Health Plan ";
    let match = str.match(/\[(\d+)\]/);

    return documentName1 + (parseInt(match[1], 10) + 1);
  } else if (str.startsWith("policies")) {
    let documentName = "Policy Document";
    const parts = str.split(".");
    const relation = parts[1];
    const policyIndex = parseInt(parts[3], 10) + 1;
    return showRelation
      ? `${documentName} ${policyIndex} for ${relation}`
      : `${documentName} ${policyIndex}`;
  } else if (str.includes("previousPolicy") && portability) {
    if (samePolicy) {
      const match = str.match(/\[(\d+)\]/);
      return match
        ? `Previous Policy ${parseInt(match[1]) + 1}`
        : "Previous Policy";
    } else {
      const match = str.match(/members\.(\d+)\.previousPolicy\.(\d+)/);
      return match
        ? `Member ${parseInt(match[1]) + 1} Previous Policy ${
            parseInt(match[2]) + 1
          }`
        : "Previous Policy";
    }
  }
  return str;
};

export const formatNumberToLakh = (number) => {
  if (typeof number !== "number") {
    return NaN; // return NaN if the argument is not a number
  }
  
  const absValue = Math.abs(number);
  
  // Check if value is in crore range (>= 1 crore = 10,000,000)
  if (absValue >= 10000000) {
    const croreValue = number / 10000000;
    // Format to 2 decimal places and remove trailing .00
    return croreValue.toFixed(2).replace(/\.00$/, "") + " Crore";
  }
  
  // For values < 1 crore, display in lakhs
  return number / 100000 + " Lakhs";
};
export const formatNumberToIndianUnit = (value) => {
  if (typeof value !== "number" || isNaN(value)) {
    return NaN;
  }

  const ABS_VALUE = Math.abs(value);

  if (ABS_VALUE >= 1_00_00_000) {
    return (value / 1_00_00_000).toFixed(2).replace(/\.00$/, "") + " Crore";
  }

  if (ABS_VALUE >= 1_00_000) {
    return (value / 1_00_000).toFixed(2).replace(/\.00$/, "") + " Lakh";
  }

  return value.toString();
};

export const getInitial = (name) => {
  const words = name.trim().split(/\s+/);

  switch (words?.length) {
    case 0:
      return "";
    case 1:
      return words[0][0]?.toUpperCase();
    case 2:
      return `${words[0][0].toUpperCase()}${words[1][0].toUpperCase()}`;
    default:
      return `${words[0][0].toUpperCase()}${words[
        words.length - 1
      ][0].toUpperCase()}`;
  }
};
export const validationForDob = (value) => {
  const d = moment(value, "DD/MM/YYYY");
  const age = moment().diff(d, "years");

  if (value && value.length !== 10) {
    return false;
  } else if (!d.isValid()) {
    return false;
  } else if (age > 100 || age <= 0) {
    return false;
  } else {
    return true;
  }
};

export const dobGreaterThanAge = (value, ageLimit) => {
  const date = moment(value, "DD/MM/YYYY");
  const age = moment().diff(date, "years");
  if (value && value.length !== 10) {
    return false;
  } else if (!date.isValid()) {
    return false;
  } else if (age < ageLimit) {
    return false;
  }
  return true;
};
export const toTitleCase = (name) => {
  if (name == "") {
    return name;
  }
  const person = name?.split(" ").filter((word) => word !== "");
  const personFullName = person?.map((word) => {
    return (
      word[0]?.toUpperCase() + word.split("").slice(1).join("").toLowerCase()
    );
  });

  return personFullName.join(" ");
};

export const getFullName = (firstName, middleName, lastName) => {
  if (firstName === "") {
    return "";
  } else {
    let fullName = "";
    if (middleName) {
      fullName = `${firstName} ${middleName} ${lastName}`;
    } else {
      fullName = `${firstName} ${lastName}`;
    }
    return toTitleCase(fullName);
  }
};

export const calculatePercentage = (activeStep, totalStep) => {
  let value = 0;
  value = activeStep * (100 / totalStep);
  return value;
};
export const convertDateFormat = (dob) => {
  if (dob === "") {
    return "";
  }
  const [day, month, year] = dob.split("/");
  return `${day}-${month}-${year}`;
};

export const convertDateToSlashFormat = (dob) => {
  if (dob === "" || !dob) {
    return "";
  }
  const [day, month, year] = dob.split("-");
  return `${day}/${month}/${year}`;
};

export const calculateTenure = (startDate, endDate) => {
  if (startDate === "" || endDate === "") {
    return "";
  }
  const format = "DD-MM-YYYY";
  const startMoment = moment(startDate, format);
  const endMoment = moment(endDate, format).add(1, "day");
  const yearsDif = endMoment.diff(startMoment, "year");
  return yearsDif;
};

export function daysToYears(days) {
  const daysInYear = 365;
  const years = Math.floor(days / daysInYear);
  return years;
}

export const hasFeb29BetweenDates = (startDate, endDate) => {
  let currentDate = moment(startDate, "DD/MM/YYYY");
  endDate = moment(endDate, "DD/MM/YYYY");
  let currYear = currentDate.year();
  let date = moment(`29/02/${currYear}`, "DD/MM/YYYY");
  while (currYear <= endDate.year()) {
    if (date.isValid() && date.isBetween(currentDate, endDate, null, [])) {
      return true;
    }
    currYear++;
    date = moment(`29/02/${currYear}`, "DD/MM/YYYY");
  }
  return false;
};

export const getInsurerName = (documentResponse, insurerArray) => {
  const firstWord = documentResponse?.documentSubType?.Value?.split(" ")[0];
  const matchingInsurer = insurerArray.find((insurer) => {
    return insurer.label.toLowerCase().includes(firstWord.toLowerCase());
  });
  return matchingInsurer ? matchingInsurer?.value : "";
};

export const sortByLabel = (arrayOfObj) => {
  return arrayOfObj.sort((a, b) => {
    if (
      a.label.split(" ")[0] === "Select" ||
      (a.label !== "Others" && b.label === "Others")
    ) {
      return -1;
    } else if (
      b.label.split(" ")[0] === "Select" ||
      (a.label === "Others" && b.label !== "Others")
    ) {
      return 1;
    } else {
      return a.label.localeCompare(b.label);
    }
  });
};

export function throttle(mainFunction, delay = 5000) {
  let timerFlag = null;
  return (...args) => {
    if (timerFlag === null) {
      mainFunction(...args);
      timerFlag = setTimeout(() => {
        timerFlag = null;
      }, delay);
    }
  };
}

export function freezePDPAddOns(optId, showPorting, productCode) {
  return (
    PDP_FREEZE_ADDONS.includes(optId) ||
    (Number(productCode) === PRODUCT_CODE_MAPPING["Super Charge"] &&
      showPorting &&
      PDP_FREEZE_ADDONS_PORT.includes(optId))
  );
}
export const findDifference = (obj1, obj2, memberArray) => {
  const insuredDocumentKeys = [
    "firstName",
    "lastName",
    "middleName",
    "title",
    "gender",
    "dob",
    "maritalStatus",
    "panNo",
  ];
  const differentKeys = [];
  const keys1 = obj1 ? Object.keys(obj1) : [];
  const keys2 = obj2 ? Object.keys(obj2) : [];
  const allKeys = new Set([...keys1, ...keys2]);
  allKeys.forEach((key) => {
    let value1 = obj1?.[key];
    let value2 = obj2?.[key];
    if (key === "cumulativeBonus") {
      value1 = parseInt(value1);
      value2 = parseInt(value2);
    }
    if (
      key === "previousPolicy" &&
      Array.isArray(value1) &&
      Array.isArray(value2)
    ) {
      if (value1.length === 0 && value2.length === 0) {
        return;
      }
    }
    if (key === "attendantDetails") {
      return;
    }

    if (String(value1) !== String(value2)) {
      if (
        memberArray &&
        insuredDocumentKeys.includes(key) &&
        obj1["relationship"] === obj2["relationship"]
      ) {
        differentKeys.push(obj1["relationship"] + "_" + key);
      } else if ((memberArray && key !== "memberStatus") || !memberArray) {
        differentKeys.push(key);
      }
    }
  });
  return differentKeys;
};

export const findNomineeDifference = (obj1, obj2) => {
  if (obj1.length !== obj2.length) {
    return ["nomineeDetails"];
  }
  const sortedObj1 = [...obj1].sort((a, b) =>
    JSON.stringify(a).localeCompare(JSON.stringify(b))
  );
  const sortedObj2 = [...obj2].sort((a, b) =>
    JSON.stringify(a).localeCompare(JSON.stringify(b))
  );
  for (let i = 0; i < sortedObj1.length; i++) {
    if (JSON.stringify(sortedObj1[i]) !== JSON.stringify(sortedObj2[i])) {
      return ["nomineeDetails"];
    }
  }
  return "";
};

export function convertToDateFormat(isoTimestamp) {
  const dateFormatRegex = /^\d{2}\/\d{2}\/\d{4}$/;
  if (isoTimestamp && !dateFormatRegex.test(isoTimestamp)) {
    const date = new Date(isoTimestamp);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  return isoTimestamp || "";
}

export const findPortingDifference = (prevDetail, newDetails) => {
  for (let i = 0; i < prevDetail.length; i++) {
    if (prevDetail[i] !== newDetails[i]) {
      return { portabilityDetailsChanged: true };
    }
  }
  return {};
};

export const findCumulativeBonusDifference = (
  prevMembers,
  newMembers,
  proposerDetails,
  proposerPrevDetails,
  isSelfIncluded,
  planType = ""
) => {
  const differences = [];

  const checkDifference = (newBonus, referenceBonus) => {
    if (newBonus !== referenceBonus) {
      differences.push("cumulativeBonus ");
    }
  };

  if (planType === PLAN_TYPES.FLOATER) {
    const referenceBonus = isSelfIncluded
      ? newMembers.find((member) => member.relationship === "Self")
          ?.cumulativeBonus
      : proposerDetails.cumulativeBonus || proposerPrevDetails.cumulativeBonus;

    newMembers.forEach((newMember) => {
      checkDifference(newMember.cumulativeBonus, referenceBonus);
    });
  } else {
    newMembers.forEach((newMember, index) => {
      const prevMember = prevMembers[index];
      checkDifference(newMember.cumulativeBonus, prevMember.cumulativeBonus);
    });
  }

  return differences;
};

export const areTwoArrayEqual = (arr1, arr2) => {
  return (
    arr1.length === arr2.length && arr1.every((item) => arr2.includes(item))
  );
};

export const ageValidate = (ageData, setAgeMsg, setShowSnackBar) => {
  let parentAge = [],
    grandParentAge = [],
    othersAge = [],
    childMember = [],
    elderMember = [];

  ageData.forEach((data) => {
    let age = calculateAgeDays(data.dob);
    if (PARENT_ARRAY.includes(data.relationship)) {
      parentAge.push(age);
      elderMember.push(age);
    } else if (GRANDPARENT_ARRAY.includes(data.relationship)) {
      grandParentAge.push(age);
      elderMember.push(age);
    } else if (CHILDREN.includes(data.relationship)) {
      childMember.push(age);
    } else {
      othersAge.push(age);
      elderMember.push(age);
    }
  });
  if (
    !isEmpty(elderMember) &&
    !isEmpty(childMember) &&
    Math.max(...childMember) >= Math.min(...elderMember)
  ) {
    setAgeMsg(QUOTE_MESSAGES.CHILD_AGE_GREATER);
    setShowSnackBar(true);
    return false;
  }
  if (
    !isEmpty(othersAge) &&
    !isEmpty(parentAge) &&
    Math.max(...othersAge) >= Math.min(...parentAge)
  ) {
    setAgeMsg(QUOTE_MESSAGES.PARENTS_AGE_GREATER);
    setShowSnackBar(true);
    return false;
  } else if (
    !isEmpty(grandParentAge) &&
    !isEmpty(parentAge) &&
    Math.max(...parentAge) >= Math.min(...grandParentAge)
  ) {
    setAgeMsg(QUOTE_MESSAGES.GRAND_PARENTS_AGE_GREATER);
    setShowSnackBar(true);
    return false;
  } else {
    setAgeMsg("");
    setShowSnackBar(false);
    return true;
  }
};

export const getSumInsured = (
  isExternalPOSP,
  producerType,
  productCode,
  variantName = ""
) => {
  if (
    isExternalPOSP ||
    producerType === PRODUCER_TYPE.POSP ||
    producerType === PRODUCER_TYPE.INTERNAL_POSP
  ) {
    return POSP_SUM_INSURED;
  }
  if (
    variantName === VARIANT_NAME.ELITE &&
    Number(productCode) === PRODUCT_CODE_MAPPING["Medicare Select"]
  ) {
    return MEDICARE_SELECT_ELITE_SUM_INSURED;
  }
  switch (Number(productCode)) {
    case PRODUCT_CODE_MAPPING["Elder Care"]:
      return ELDERCARE_SUM_INSURED;
    case PRODUCT_CODE_MAPPING["Super Charge"]:
      return SUPER_CHARGE_SUM_INSURED;
    case PRODUCT_CODE_MAPPING["Medicare Plus"]:
      return MEDICARE_PLUS_SUM_INSURED;
    case PRODUCT_CODE_MAPPING["MediCare LITE"]:
      return MEDICARE_LITE_SUM_INSURED;
    case PRODUCT_CODE_MAPPING["Medicare"]:
      return MEDICARE_SUM_INSURED;
    case PRODUCT_CODE_MAPPING["Medicare Premier"]:
      return MEDICARE_PREMIER_SUM_INSURED;
    case PRODUCT_CODE_MAPPING["Medicare Select"]:
      return MEDICARE_SELECT_SUM_INSURED;
    case PRODUCT_CODE_MAPPING["Medicare Reserve"]:
      return MEDICARE_RESERVE_SUM_INSURED;
    default:
      return SUM_INSURED_OPTIONS;
  }
};
export const valueConvert = (val) => {
  return val.split("_").join(" ");
};
export const dateValidator = (value) => {
  if (value && value.length !== 10) {
    return false;
  }
  const d = moment(value, "DD/MM/YYYY");
  if (d.isValid()) {
    return true;
  }
  return false;
};
export const extractValues = (arr, key) => {
  return arr.map((item) => item[key]);
};

export const areAllValuesEmpty = (details = {}) => {
  return Object.values(details).every((value) => value === "");
};
export const isFieldRequired = (obj) =>
  Object.values(obj || {}).some((val) => val?.toString().trim() !== "");

export const isValidAgeForProductDobEndorsement = (
  relationship,
  dob,
  productCode,
  referenceDate = null,
  planType
) => {
  const age = calculateAge(dob, "", "DD/MM/YYYY", referenceDate);
  const ageDays = calculateAgeDays(dob);
  if (age === null) return false;

  if (Number(productCode) === PRODUCT_CODE_MAPPING["Medicare Select"]) {
    if (
      DEPENDENT_CHILDREN.includes(relationship) &&
      planType === PLAN_TYPES.FLOATER
    ) {
      return ageDays >= 1 && age <= 25;
    } else {
      return ageDays >= 1 && age <= 100;
    }
  } else if (Number(productCode) === PRODUCT_CODE_MAPPING["Medicare Reserve"]) {
    if (DEPENDENT_CHILDREN.includes(relationship)) {
      // Dependent children: 0-25 for Floater, 0-18 for Individual
      if (planType === PLAN_TYPES.FLOATER) {
        return ageDays >= 0 && age <= 25;
      } else {
        return ageDays >= 0 && age <= 18;
      }
    } else {
      // Adults: 18-55 years
      return age >= 18 && age <= 55;
    }
  } else {
    if (
      DEPENDENT_CHILDREN.includes(relationship) &&
      planType === PLAN_TYPES.FLOATER
    ) {
      return ageDays >= 91 && age <= 25;
    } else return ageDays >= 91 && age <= 65;
  }
};

export const isValidAgeForProduct = (
  dob,
  productCode,
  referenceDate = null
) => {
  const age = calculateAge(dob, "", "DD/MM/YYYY", referenceDate);
  const ageDays = calculateAgeDays(dob, "DD/MM/YYYY", referenceDate);
  if (age === null) return false;

  if (Number(productCode) === PRODUCT_CODE_MAPPING["Elder Care"]) {
    return age >= 61 && age <= 85;
  }

  if (Number(productCode) === PRODUCT_CODE_MAPPING["Medicare Select"]) {
    return ageDays >= 1 && age <= 100;
  }

  if (Number(productCode) === PRODUCT_CODE_MAPPING["Medicare Reserve"]) {
    // Medicare Reserve: 0-55 years (1 day to 55 years)
    return ageDays >= 0 && age <= 55;
  }

  // Other products
  return ageDays >= 91 && age <= 65;
};

export const transformPathsToBracketNotation = (paths) => {
  return paths.map((path) => path.replace(/\.(\d+)(?=\.|$)/g, "[$1]"));
};
export const policyCreatedBefore21Aug = (policyStartDate) => {
  if (policyStartDate) {
    const [day, month, year] = policyStartDate.split("/").map(Number);
    const createdDate = new Date(year, month - 1, day);
    const cutoffDate = new Date(2025, 7, 21);
    return createdDate < cutoffDate;
  }
  return false;
};

export const checkIfAgeisLessThen1Years = (
  newProposalData,
  oldProposalData
) => {
  if (newProposalData?.category === "CAT06") {
    const newDob = moment(newProposalData.data.proposer.dob, "DD/MM/YYYY");
    const oldDob = moment(oldProposalData?.proposer?.dob, "DD/MM/YYYY");
    const diffYears = Math.abs(newDob.diff(oldDob, "years", true));

    if (newDob.isSameOrAfter(oldDob) && diffYears >= 1) {
      return true;
    }
    return false;
  } else if (newProposalData?.category === "CAT07") {
    return oldProposalData.memberDetails.some((oldMember) => {
      const newMember = newProposalData.data.memberDetails.find(
        (m) => m.memberId === oldMember.memberId
      );
      if (!newMember) return false;
      const newDob = moment(newMember.dob, "DD/MM/YYYY");
      const oldDob = moment(oldMember.dob, "DD/MM/YYYY");
      const diffYears = Math.abs(newDob.diff(oldDob, "years", true));
      if (newDob.isSameOrAfter(oldDob) && diffYears >= 1) {
        return true;
      }
      return false;
    });
  } else {
    return false;
  }
};
export const slotDisplayLogic = (selectedDate) => {
  const now = dayjs();
  const date = dayjs(selectedDate);
  const currentHour = now.hour();
  const currentMinute = now.minute();

  // --- All available slots ---
  const allSlots = [
    { id: 1, label: "10:00 AM - 12:00 PM", value: "10:00 AM - 12:00 PM" },
    { id: 2, label: "12:00 PM - 2:00 PM", value: "12:00 PM - 2:00 PM" },
    { id: 3, label: "2:00 PM - 4:00 PM", value: "2:00 PM - 4:00 PM" },
    { id: 4, label: "4:00 PM - 6:00 PM", value: "4:00 PM - 6:00 PM" },
    { id: 5, label: "5:00 PM - 6:00 PM", value: "5:00 PM - 6:00 PM" }, // special
  ];

  const isSameDay = date.isSame(now, "day");

  // --- Disable past days & Sundays ---
  if (date.isBefore(now, "day") || date.day() === 0) {
    return { availableSlots: [], disableDate: true };
  }

  // --- Future date (show all slots) ---
  if (!isSameDay) {
    return { availableSlots: allSlots.slice(0, 4), disableDate: false };
  }

  const currentTimeInHours = currentHour + currentMinute / 60;

  // --- Scenario 1: Grace Period Logic ---
  const gracePeriods = [
    { start: 10, end: 10.5, slotId: 2 },
    { start: 12, end: 12.5, slotId: 3 },
    { start: 14, end: 14.5, slotId: 4 },
  ];

  const graceMatch = gracePeriods.find(
    (g) => currentTimeInHours >= g.start && currentTimeInHours < g.end
  );
  if (graceMatch) {
    let startIndex = allSlots.findIndex((s) => s.id === graceMatch.slotId);
    return {
      availableSlots: allSlots.slice(startIndex, 4),
      disableDate: false,
    };
  }

  // --- Scenario 2: 2-hour ahead logic (before 2:30 PM) ---
  if (currentTimeInHours < 14.5) {
    const twoHoursAhead = now.add(2, "hour");
    const aheadHour = twoHoursAhead.hour() + twoHoursAhead.minute() / 60;

    let availableSlots = [];
    if (aheadHour < 12) {
      availableSlots = [allSlots[1]];
    } else if (aheadHour < 14) availableSlots = allSlots.slice(2, 4);
    else if (aheadHour < 16) availableSlots = allSlots.slice(3, 4);
    else if (currentTimeInHours >= 14.5 && currentTimeInHours < 16) {
      availableSlots = allSlots.slice(4, 5);
    }
    return { availableSlots: availableSlots, disableDate: false };
  }

  // --- Scenario 3: Uniform fallback (2:30–4:00 PM) ---
  if (currentTimeInHours >= 14.5 && currentTimeInHours < 16) {
    return { availableSlots: [allSlots[4]], disableDate: false }; // only 5–6
  }

  // --- After 4:00 PM (no same-day reschedule) ---
  if (currentTimeInHours >= 16) {
    return { availableSlots: [], disableDate: true, nextDay: true };
  }

  return { availableSlots: allSlots.slice(0, 3), disableDate: false };
};
export const calculateSumInsured = (min, max, defaultValue = null) => {
  if (min == null || max == null || max < min) return [];

  const rawValues = new Set();
  if (defaultValue && defaultValue > 0) rawValues.add(defaultValue);
  if (min > 0) rawValues.add(min);
  let current = min > 0 ? min : 0;
  while (current < max) {
    let interval;
    if (current >= 500000) interval = 500000;
    else if (current >= 100000) interval = 100000;
    else if (current >= 10000) interval = 10000;
    else interval = 1000;

    current += interval;

    if (current > 0 && current <= max) {
      rawValues.add(current);
    }

    if (rawValues.size > 500) break;
  }
  if (max > 0) rawValues.add(max);
  const formatLabel = (num) => {
    if (num >= 10000000) return `${num / 10000000} crore`;
    if (num >= 100000) return `${num / 100000} lacs`;
    if (num >= 1000) return `${num / 1000}k`;
    return `${num}`;
  };
  return Array.from(rawValues)
    .sort((a, b) => a - b)
    .map((value, index) => ({
      id: index + 1,
      label: formatLabel(value),
      value,
    }));
};

export const formattedOptionsSumInsured = (arrayValues, min, max) =>
  arrayValues
    .filter((val) => val >= min && val <= max)
    .map((val, index) => ({
      id: index + 1,
      label: val,
      value: val,
    }));

export const formattedOptions = (arrayValues) =>
  arrayValues.map((val, index) => ({
    id: index + 1,
    label: val,
    value: val,
  }));

export const getProductKey = (product) =>
  `${product.productCode}_${product.variantCode || ""}_${
    product.secondaryProductCode || ""
  }`;

export const toBoolean = (val) => val === true || val === "Yes";

export const toNumber = (val) =>
  val === undefined || val === null || val === "" ? undefined : Number(val);

export const normalizeDate = (date) => date || "";
export const extractGroupFromCover = (coverCode) => {
  const match = coverCode.match(/_G(\d+)$/);
  return match ? match[1] : null;
};

export const extractGroupFromPackage = (basePackage) => {
  const match = String(basePackage).match(/group\s*(\d+)/i);
  return match ? match[1] : null;
};

export function hasValidLoanDetails(responseApi, result) {
  return responseApi?.memberDetails?.every((member) => {
    const { relationship, loanAmount, monthlyEMI } = member;

    // Only validate members who actually have loan data
    if (!loanAmount || !monthlyEMI) return true;

    const memberLoans =
      result?.additionalDetails?.loanDetails?.members?.[relationship]?.loans;

    // No loans array or empty array
    if (isEmpty(memberLoans?.[0])) {
      return false;
    }

    // Check if every loan object is empty {}
    const hasAtLeastOneValidLoan = memberLoans?.some(
      (loan) => Object?.keys(loan)?.length > 0
    );

    return hasAtLeastOneValidLoan;
  });
}

export function isNotDobEndorsement(typeOfEndorsement) {
  return [
    ENDORSEMENT_ACTIONS.ADD_MEMBER,
    ENDORSEMENT_ACTIONS.DELETE_MEMBER,
    ENDORSEMENT_ACTIONS.EDIT_LIFESTYLE_DECLARATION,
    ENDORSEMENT_ACTIONS.EDIT_MEDICAL_DECLARATION,
    ENDORSEMENT_ACTIONS.EDIT_PORTABILITY_DETAILS,
    ENDORSEMENT_ACTIONS.EDIT_NOMINEE_DETAIL,
    ENDORSEMENT_ACTIONS.EDIT_PROPOSER_MOBILE,
    ENDORSEMENT_ACTIONS.EDIT_PROPOSER_EMAIL,
    ENDORSEMENT_ACTIONS.CUMULATIVE_BONUS,
    ENDORSEMENT_ACTIONS.EDIT_INSURED_DETAILS,
    ENDORSEMENT_ACTIONS.EDIT_PROPOSER_DETAIL_WITHOUT_PINCODE,
  ].includes(typeOfEndorsement);
}

export const isValidAgeForProductPA = (
  relationship,
  dob,
  referenceDate = null
) => {
  const age = calculateAge(dob, "", "DD/MM/YYYY", referenceDate);
  const ageDays = calculateAgeDays(dob, "DD/MM/YYYY", referenceDate);
  if (age === null) return false;

  if (DEPENDENT_CHILDREN.includes(relationship)) {
    return ageDays >= 91 && age <= 25;
  } else {
    return age >= 18 && age <= 70;
  }
};

export const formatZone = (zone) => {
  return zone?.replace(/^Zone\s+/i, "") || "";
};
